<script setup lang="ts">
import { ref, onMounted } from 'vue';
import FastSetSvg from '@/assets/svg/home/page3/page3-fast-set.svg';
import MyselfSvg from '@/assets/svg/home/page3/page3-myself.svg';
import TextSvg from '@/assets/svg/home/page3/page3-text.svg';
import VoiceSvg from '@/assets/svg/home/page3/page3-voice.svg';
import IconSvg from '@/assets/svg/home/page3/page3-icon.svg';
import PlaySvg from '@/assets/svg/home/page3/page3-play.svg';
import MyselfBgSvg from '@/assets/svg/home/page3/page3-myself-bg.svg';
// 图片资源
import ClonePeoplePng from '@/assets/img/home/page3/page3-clone-people.png';
import ClonePng from '@/assets/img/home/page3/page3-clone.png';
import FacePng from '@/assets/img/home/page3/page3-face.png';
import RealPeoplePng from '@/assets/img/home/page3/page3-reale-people.png';
import WavePng from '@/assets/img/home/page3/page3-wave.png';
import TextBgPng from '@/assets/img/home/page3/page3-text-bg.png';
import TextChatPng from '@/assets/img/home/page3/page3-text-chat.png';
import pageOemSvg from "@/assets/svg/digital-human-oem/page1-oem.svg" 
import { useI18n } from 'vue-i18n'
import type { TabContent } from '@/config';
import { goToWorkspace } from '@/utils/redirect';
const { t } = useI18n()

const activeTab = ref(0);
const isVisible = ref(false);
const contentVisible = ref(true);
const pageRef = ref<HTMLElement | null>(null);

const tabContents: TabContent[] = [
  {
    id: 0,
    icon: VoiceSvg,
    titleKey: 'page3.tabs.voice',
    mainTitleKey: 'page3.tabs.voiceMain',
    features: [
      { titleKey: 'page3.voice.f1.t', descKey: 'page3.voice.f1.d' },
      { titleKey: 'page3.voice.f2.t', descKey: 'page3.voice.f2.d' },
      { titleKey: 'page3.voice.f3.t', descKey: 'page3.voice.f3.d' }
    ],
    image: ClonePeoplePng,
    extraContent: {
      type: 'voice',
      audioSamples: [
        { avatar: FacePng, titleKey: 'page3.voice.origin', wave: WavePng },
        { avatar: ClonePng, titleKey: 'page3.voice.clone', wave: WavePng }
      ],
      compareImage: RealPeoplePng
    }
  },
  {
    id: 1,
    icon: TextSvg,
    titleKey: 'page3.tabs.text',
    mainTitleKey: 'page3.tabs.textMain',
    features: [
      { titleKey: 'page3.text.f1.t', descKey: 'page3.text.f1.d' },
      { titleKey: 'page3.text.f2.t', descKey: 'page3.text.f2.d' }
    ],
    image: TextBgPng,
    extraContent: {
      type: 'text',
      chatImage: TextChatPng
    }
  },
  {
    id: 2,
    icon: FastSetSvg,
    titleKey: 'page3.tabs.deploy',
    mainTitleKey: 'page3.tabs.deployMain',
    features: [
      { titleKey: 'page3.deploy.f1.t', descKey: 'page3.deploy.f1.d' },
      { titleKey: 'page3.deploy.f2.t', descKey: 'page3.deploy.f2.d' }
    ],
    image: pageOemSvg 
  },
  {
    id: 3,
    icon: MyselfSvg,
    titleKey: 'page3.tabs.private',
    mainTitleKey: 'page3.tabs.privateMain',
    features: [
      { titleKey: 'page3.deploy.f1.t', descKey: 'page3.deploy.f1.d' },
      { titleKey: 'page3.deploy.f2.t', descKey: 'page3.deploy.f2.d' }
    ],
    extraContent: {
      type: 'myself',
      ImageSvg: MyselfBgSvg
    },
    image: MyselfBgSvg
  }
]

const handleTabClick = (id: number) => {
  if (activeTab.value === id) return;

  // 先隐藏当前内容
  contentVisible.value = false;

  // 等待动画完成后再切换标签和显示新内容
  setTimeout(() => {
    activeTab.value = id;
    setTimeout(() => {
      contentVisible.value = true;
    }, 50);
  }, 350); // 调整过渡时间到350ms
};

// 监听页面可见性
onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isVisible.value = true;
          setTimeout(() => {
            contentVisible.value = true;
          }, 300);
        } else {
          isVisible.value = false;
          contentVisible.value = false;
        }
      });
    },
    {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    }
  );

  if (pageRef.value) {
    observer.observe(pageRef.value);
  }
});
</script>

<template>
  <div ref="pageRef">
    <!-- 标题 -->
    <div class="flex flex-col mt-[6.3rem] mb-[2.2rem] justify-center items-center">
      <div class="text-[3rem] font-[600] text-main-primary transform transition-all duration-300"
        :class="isVisible ? 'fade-in' : 'fade-out'">
        {{ t('page3.titleMain') }}
      </div>
      <div class="text-[1.25rem] font-[600] transform transition-all duration-300 delay-200"
        :class="isVisible ? 'fade-in' : 'fade-out'">
        {{ t('page3.titleSub') }}
      </div>
    </div>

    <!-- Tab 导航 -->
    <div
      class="flex max-width mx-auto justify-center items-center gap-[5rem] mb-[3rem] border-b-[0.5px] border-[#D8D8D8]">
      <div v-for="tab in tabContents" :key="tab.id" class="flex hover:scale-[1.05] px-[1rem] rounded-lg transition-all duration-300 
               flex-col items-center cursor-pointer group" :class="isVisible ? 'fade-in' : 'fade-out'"
        :style="{ transitionDelay: `${400 + tab.id * 100}ms` }" @click="handleTabClick(tab.id)">
        <div class="w-[4.5rem] h-[4.5rem] flex justify-center items-center transition-all duration-300">
          <component :is="tab.icon"
            class="w-[5.1rem] h-[5.1rem] transition-all duration-300 group-hover:text-main-primary" :class="{
              'text-main-primary scale-110': activeTab === tab.id,
              'fade-out': !isVisible
            }" />
        </div>

        <div class="text-[0.875rem] mb-3 mt-4 transition-colors duration-300 text-[#3D3D3D] 
                 group-hover:text-main-primary" :class="{ 'text-main-primary': activeTab === tab.id }">
          {{ t(tab.titleKey) }}
        </div>

        <div class="h-[0.25rem] w-full rounded-full transition-all duration-300 
                 group-hover:bg-main-primary/50" :class="{
                  'bg-main-primary': activeTab === tab.id,
                  'bg-transparent': activeTab !== tab.id
                }" />
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="mt-[2rem] max-width mx-auto relative overflow-hidden">
      <div :key="activeTab" class="flex h-[38rem] justify-between w-full">
        <!-- 左侧内容 -->
        <div class="w-[40%] flex flex-col mt-[4rem]">
          <div class="text-[1.875rem] font-[600] text-[#3D3D3D] transform transition-all duration-300" :class="[
            isVisible ? 'slide-left-in' : 'slide-left-out',
            contentVisible ? 'slide-left-in' : 'slide-left-out'
          ]">
            <span class="text-main-primary">{{ t(tabContents[activeTab].mainTitleKey).split(' ')[0] }}</span>
            {{ t(tabContents[activeTab].mainTitleKey).split(' ').slice(1).join(' ') }}
          </div>

          <!-- 分隔线 -->
          <div class="flex w-full h-[0.5px] bg-[#D8D8D8] items-center my-[2.7rem] relative">
            <div class="w-[5rem] h-[3px] bg-[#3D3D3D] absolute divider" :style="{
              transform: `translateX(calc(${activeTab} * 7rem))`,
              transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)'
            }"></div>
          </div>

          <!-- 特性列表 -->
          <div class="space-y-[1.8rem]">
            <div v-for="(f, i) in tabContents[activeTab].features" :key="i" class="transition-all"
              :class="[isVisible ? 'slide-left-in' : 'slide-left-out', contentVisible ? 'slide-left-in' : 'slide-left-out']"
              :style="{ transitionDelay: `${contentVisible ? 50 + i * 50 : 0}ms` }">
              <div class="flex items-center gap-4">
                <div class="w-[1.5rem] h-[1.5rem]">
                  <IconSvg />
                </div>
                <div class="text-[1.125rem] text-[#3D3D3D]">{{ t(f.titleKey) }}</div>
              </div>
              <div class="text-[#7C7C7C] ml-[2.25rem]">{{ t(f.descKey) }}</div>
            </div>
          </div>
          <div
            :class="[isVisible ? 'slide-left-in' : 'slide-left-out', contentVisible ? 'slide-left-in' : 'slide-left-out']"
            :style="{ transitionDelay: `${contentVisible ? 50 : 0}ms` }"
            v-if="tabContents[activeTab].extraContent?.type === 'voice'" class="flex mt-[4rem]">
            <div
              class="text-[1rem] hover-button hover:bg-main-primary/80 !py-[0.9rem] !px-[2.5rem] text-[#fff] bg-main-primary rounded-[0.5rem] cursor-pointer"
              @click="goToWorkspace()">{{ t('page3.enterWorkspace') }}</div>
          </div>
        </div>
        <!-- 右侧演示区域 -->
        <div class="w-[50%] mt-[4rem] flex justify-end">
          <div class="flex relative h-[24.4rem] items-center mr-[3rem]">
            <!-- 声音克隆特殊内容 -->
            <template v-if="tabContents[activeTab].extraContent?.type === 'voice'">
              <div class="absolute z-[1] bottom-[2.5rem] left-[-8rem] transform transition-all duration-300" :class="[
                isVisible ? 'fade-in' : 'fade-out',
                contentVisible ? 'fade-in' : 'fade-out'
              ]">
                <div class="flex flex-col gap-[1rem]">
                  <div v-for="(sample, index) in tabContents[activeTab].extraContent.audioSamples" :key="index"
                    class="flex hover:scale-[1.05] transition-all duration-300 items-center w-[15rem] p-[0.8rem] shadow-lg gap-[1rem] bg-white rounded-lg">
                    <div class="flex items-center gap-[0.5rem]">
                      <img :src="sample.avatar" class="w-[2.5rem] h-[2.5rem] rounded-full" :alt="sample.title" />
                      <span class="text-[#333] text-[1rem]">{{ sample.title }}</span>
                    </div>
                    <div class="flex-1 flex items-center">
                      <div class="flex-1 h-[2rem] flex items-center">
                        <img :src="sample.wave" class=" w-full h-[1.5rem] opacity-60" alt="波形" />
                      </div>
                      <div class="w-[2.68rem] cursor-pointer h-[2.68rem] hover:scale-[1.05] ml-[1rem]">
                        <PlaySvg />
                        <!-- <StopSvg/> -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="w-[15rem] bg-white h-[23.6rem] border-[2px] overflow-hidden rounded-[11px] border-main-primary/30 transform transition-all duration-300"
                :class="[
                  isVisible ? 'slide-right-in' : 'slide-right-out',
                  contentVisible ? 'slide-right-in' : 'slide-right-out'
                ]">
                <img :src="tabContents[activeTab].image"
                  class="hover:scale-[1.05] w-[15rem] h-[23.6rem] object-cover" />
              </div>
              <div
                class="ml-[2rem]  relative w-[15rem] h-[23.6rem] border-[2px]  border-main-primary/30 overflow-hidden rounded-[11px] "
                :class="[
                  isVisible ? 'slide-right-in' : 'slide-right-out',
                  contentVisible ? 'slide-right-in' : 'slide-right-out'
                ]">
                <img :src="tabContents[activeTab].extraContent.compareImage"
                  class="absolute   z-[1]  left-0 top-0 mr-[3rem] w-[15rem] h-[23.6rem] object-cover" />
                <img :src="tabContents[activeTab].image"
                  class="absolute    z-[1] left-0 top-0 w-[15rem] h-[23.6rem] object-cover hover:scale-[1.05] " />
              </div>
            </template>

            <!-- 智能文案特殊内容 -->
            <template v-else-if="tabContents[activeTab].extraContent?.type === 'text'">
              <div class="absolute z-[99] right-0 top-[45%] transform transition-all duration-300" :class="[
                isVisible ? 'fade-in' : 'fade-out',
                contentVisible ? 'fade-in' : 'fade-out'
              ]">
                <img :src="tabContents[activeTab].extraContent.chatImage" class="mr-[0rem] h-[10rem]" />
              </div>
              <img :src="tabContents[activeTab].image"
                class="mr-[3rem]  w-[46rem] h-[24.4rem] object-contain transform transition-all duration-300" :class="[
                  isVisible ? 'slide-right-in' : 'slide-right-out',
                  contentVisible ? 'slide-right-in' : 'slide-right-out'
                ]" />
            </template>
            <!-- 定制化特殊内容 -->
            <template v-else-if="tabContents[activeTab].extraContent?.type === 'myself'">

              <MyselfBgSvg class="w-[39rem] h-[25.75rem] object-contain transform transition-all duration-300" :class="[
                isVisible ? 'slide-right-in' : 'slide-right-out',
                contentVisible ? 'slide-right-in' : 'slide-right-out'
              ]" />
            </template>
            <!-- 默认内容展示 -->
            <template v-else>
              <pageOemSvg
                class="mr-[3rem] w-[46rem] h-[24.4rem] object-contain transform transition-all duration-300" :class="[
                  isVisible ? 'slide-right-in' : 'slide-right-out',
                  contentVisible ? 'slide-right-in' : 'slide-right-out'
                ]" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hover-button {
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.hover-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 基础样式 */
.group {
  position: relative;
  isolation: isolate;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.group::after {
  content: '';
  position: absolute;
  inset: 0;
  background-color: var(--el-color-primary);
  opacity: 0;
  z-index: -1;
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0.5rem;
}

.group:hover::after {
  opacity: 0.1;
}

/* 切换动画 */
.transform {
  transition-property: transform, opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
  transition-duration: 300ms;
}

/* 添加动画效果 */
.fade-in {
  opacity: 1;
  transform: scale(1) translateZ(0);
}

.fade-out {
  opacity: 0;
  transform: scale(0.95) translateZ(0);
}

.slide-left-in {
  opacity: 1;
  transform: translateX(0) translateZ(0);
}

.slide-left-out {
  opacity: 0;
  transform: translateX(-20px) translateZ(0);
}

.slide-right-in {
  opacity: 1;
  transform: translateX(0) translateZ(0);
}

.slide-right-out {
  opacity: 0;
  transform: translateX(20px) translateZ(0);
}

/* 优化图片过渡效果 */
img {
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
}

img:hover {
  transform: scale(1.02) translateZ(0);
}

/* 分隔线动画 */
.divider {
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
}
</style>